import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./Admin.module.css";
import { ErrorState, LoadingState } from "../states";
import { useToast } from "../toast/toastContext";
import { ProviderAllocationEditor } from "../allocation/ProviderAllocationEditor";
import { formatUsd, toDraft, toItems } from "../allocation/allocationDraft";
import { useAccessGroupCatalog } from "../allocation/useAccessGroupCatalog";
import { useAllocationForm } from "../allocation/useAllocationForm";
import {
  fetchProviderAllocation,
  saveProviderAllocation,
  type ProviderAllocationOverview,
} from "../../api/providerAllocation";

const AdministerPermission = "entitlement.administer";

type LoadState =
  | { kind: "loading" }
  | { kind: "loaded"; overview: ProviderAllocationOverview }
  | { kind: "error"; message: string };

/**
 * The per-user provider allocation panel (issue #27, CON-27 to CON-33). The server owns the total,
 * fees and committed cost; this panel only re-derives the figures as the administrator toggles providers
 * so over-commitment is blocked before the request. The backend validates the same rule and its reasons
 * are shown verbatim if it still refuses. The form itself is shared with the engineer's own panel.
 */
export function PersonProviderAllocationPanel({
  personId,
  onSaved,
}: {
  personId: string;
  onSaved?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const overview = state.kind === "loaded" ? state.overview : null;
  const form = useAllocationForm(overview);
  const accessGroupCatalog = useAccessGroupCatalog();
  const { reset } = form;
  const [isSaving, setIsSaving] = useState(false);
  const [resplit, setResplit] = useState(false);

  const load =
    useCallback(async (): Promise<ProviderAllocationOverview | null> => {
      const result = await fetchProviderAllocation(personId);
      if (result.kind === "ok") {
        setState({ kind: "loaded", overview: result.response });
        reset(toDraft(result.response));
        return result.response;
      }

      setState({
        kind: "error",
        message:
          result.kind === "denied"
            ? t("admin.denied.description", {
                permissions: AdministerPermission,
              })
            : result.message,
      });
      return null;
    }, [personId, reset, t]);

  useEffect(() => {
    setState({ kind: "loading" });
    void load();
  }, [load]);

  const handleSave = async (): Promise<void> => {
    if (!overview || !form.canSubmit) {
      return;
    }

    setIsSaving(true);
    const submitted = toItems(overview, form.draft);
    const result = await saveProviderAllocation(personId, submitted);
    setIsSaving(false);
    if (result.kind === "ok") {
      toast.show(t("admin.entitlement.person.allocation.saved"), "success");
      onSaved?.();
      const reloaded = await load();
      setResplit(
        reloaded?.providers.some((provider) => {
          const sent = submitted.find(
            (item) => item.vendorId === provider.vendorId,
          );
          return (
            sent !== undefined &&
            (sent.enabled !== provider.enabled ||
              sent.amountUsd !== provider.amountUsd)
          );
        }) === true,
      );
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: AdministerPermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  const headingId = `provider-allocation-${personId}`;
  const consumedThisMonth = overview ? overview.providers.reduce((sum, provider) => sum + (provider.incurredCostUsd ?? 0), 0) : 0;

  return (
    <section
      className={`${styles.field} ${styles.allocationBox}`}
      aria-labelledby={headingId}
    >
      <div className={styles.allocationHeader}>
        <h3 id={headingId}>{t("admin.entitlement.person.allocation.title")}</h3>
        {overview && (
          <span className={styles.allocationConsumed}>
            {t("admin.entitlement.person.allocation.consumedThisMonth")}:{" "}
            <strong>{formatUsd(consumedThisMonth)}</strong>
          </span>
        )}
      </div>
      {state.kind === "loading" && (
        <LoadingState
          label={t("admin.entitlement.person.allocation.loading")}
        />
      )}
      {state.kind === "error" && <ErrorState description={state.message} />}
      {overview && form.figures && (
        <>
          {resplit && (
            <p role="status" className={styles.note}>
              {t("admin.entitlement.person.allocation.resplitNotice")}
            </p>
          )}
          {!overview.hasAllocation && (
            <p role="status" className={styles.note}>
              {t("admin.entitlement.person.allocation.noAllocation")}
            </p>
          )}

          <ProviderAllocationEditor
            overview={overview}
            draft={form.draft}
            figures={form.figures}
            idPrefix={`allocation-amount-${personId}`}
            accessGroupCatalog={accessGroupCatalog}
            onChange={form.setEntry}
          />

          <div className={styles.allocationActions}>
            <button
              type="button"
              className="button button-primary"
              onClick={() => void handleSave()}
              disabled={isSaving || !form.canSubmit}
            >
              {t("admin.entitlement.person.allocation.save")}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
